/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { TableSkeleton } from "../../../TableSkeleton";
import clsx from "clsx";
import "../../DataTable.css";
import { TablePaginationActions } from "../../TablePaginationActions";
import { ICustomTableProps } from "./interfaces/ICustomTableProps";
import { CustomCheckbox } from "../../../formsComponents";
import { AutoDeleteOutlined, DeleteOutline, RemoveDoneOutlined } from "@mui/icons-material";
import { SelectAll } from "../SelectAll/SelectAll";
import { TABLE_ROW_TYPE } from "../../interfaces/IDataTableProps";
import { getUniqueId } from "../../../../helpers";

export const CustomTable: React.FC<ICustomTableProps> = (props) => {
  const {
    columns,
    items,
    selectedRows,
    selectType,
    uniqueRowKeyName,
    isDataLoading,
    loaderSkeletonRows,
    selectionMode,
    isPagination,
    rowsPerPageOptions,
    totalRecords,
    onRenderColumnHeader,
    onTableRowCellRender,
    onPageChange,
    onRowsPerPageChange,
    onSelectionAllRows,
    onSelectRow,
    onMultipleSelectedItemsAction,
  } = props;

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(rowsPerPageOptions);
  const currentPageItems =
    !totalRecords && rowsPerPage > 0
      ? items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : items;

  const handlePageChange = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    if (onPageChange) {
      onPageChange(newPage + 1);
    }
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const rowsPerPage = parseInt(event.target.value, 10);
    if (onRowsPerPageChange) {
      onRowsPerPageChange(
        1,
        rowsPerPage === -1 ? totalRecords || items.length : rowsPerPage
      );
    }
    setRowsPerPage(rowsPerPage);
    setPage(0);
  };

  return (
    <Card
      className="tableScroll"
      sx={{
        borderRadius: "5px",
        boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 3px",
        border: 0,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        padding: 0,
        marginTop: 0,
        display: "flex",
        flexDirection: "column",
        maxHeight: "calc(100vh - 180px)",
        position: "relative",
      }}
    >
      <CardContent
        sx={{
          padding: "0px !important",
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "auto",
        }}
      >
        <Box
          sx={{
            overflowY: "auto",
            overflowX: "auto",
          }}
        >
          {(selectedRows.length > 0 || selectType === "AllPages") && (
            <Box
              sx={{
                padding: 2,
                borderRadius: 1,
                m: 2,
                mt: 0,
                backgroundColor: "primary.light",
              }}
            >
              <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Cancle Selection">
                    <RemoveDoneOutlined
                      sx={{ ml: 1, cursor: "pointer" }}
                      fontSize="small"
                      color="primary"
                      onClick={() => onSelectionAllRows(false)}
                    />
                  </Tooltip>
                  {
                    onMultipleSelectedItemsAction && (
                      <Tooltip title="Delete/ Undo">
                        <AutoDeleteOutlined
                          sx={{ ml: 1, cursor: "pointer" }}
                          fontSize="small"
                          color="error"
                          onClick={() => onMultipleSelectedItemsAction(selectedRows)}
                        />
                      </Tooltip>
                    )
                  }
                </Box>
            </Box>
          )}
          <Table
            stickyHeader
            aria-label="simple table"
            sx={{
              whiteSpace: "nowrap",
            }}
          >
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 5,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark" ? "rgb(31,41,55)" : "#F3F4F6",
              }}
            >
              <TableRow>
                {selectionMode === "multiple" && (
                  <TableCell key="selection">
                    <SelectAll
                      checkedType={selectType}
                      onChange={onSelectionAllRows}
                    />
                  </TableCell>
                )}
                {columns.map((column) => {
                  return (
                    <TableCell key={column.key}>
                      {onRenderColumnHeader(column)}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {isDataLoading && (
                <TableSkeleton
                  numberOfCells={
                    selectionMode !== "none"
                      ? columns.length + 1
                      : columns.length
                  }
                  numberOfRows={loaderSkeletonRows}
                />
              )}

              {!isDataLoading &&
                currentPageItems.map((row, index) => {
                  const columnsIndexes = Array.from(
                    Array(columns.length).keys()
                  );

                  return (
                    <CTableRow
                      key={
                        uniqueRowKeyName ? row[uniqueRowKeyName] : getUniqueId()
                      }
                      uniqueRowKeyName={uniqueRowKeyName}
                      onTableRowCellRender={onTableRowCellRender}
                      index={index}
                      row={row}
                      selectionMode={selectionMode}
                      selectType={selectType}
                      selectedRows={selectedRows}
                      onSelectRow={onSelectRow}
                      columnsIndexes={columnsIndexes}
                      columns={columns}
                    />
                  );
                })}
            </TableBody>
            {isPagination && (
              <TableFooter
                sx={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 10,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "rgb(31,41,55)" : "#ffffff",
                  borderTop: (theme) =>
                    `1px solid ${
                      theme.palette.mode === "dark" ? "#374151" : "#e5e7eb"
                    }`,
                }}
              >
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
                    sx={{
                      borderBottom: 0,
                      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                        color: "#6c85b1",
                        fontSize: "0.875rem",
                      },
                      "& .MuiTablePagination-select": {
                        color: "#6c85b1",
                        fontSize: "0.875rem",
                      },
                    }}
                    colSpan={columns.length + 1}
                    count={totalRecords || items.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      native: true,
                    }}
                    labelDisplayedRows={() => `${items?.length || 0} rows currently shown`}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
};

const CTableRow = React.memo(
  ({
    uniqueRowKeyName,
    onTableRowCellRender,
    index,
    row,
    selectionMode,
    selectType,
    selectedRows,
    onSelectRow,
    columnsIndexes,
    columns,
  }: any) => {
    const data = React.useMemo(() => {
      return (
        <TableRow
          sx={{
            ":hover": {
              background: "rgba(0, 0, 0, 0.03)",
            },
          }}
          className={clsx({ "row-disabled": row.isDisabled })}
        >
          {selectionMode !== "none" && (
            <TableCell>
              <CustomCheckbox
                disabled={selectType === "AllPages"}
                checked={
                  selectedRows.findIndex(
                    (x: any) => x[uniqueRowKeyName] === row[uniqueRowKeyName]
                  ) > -1 || selectType === "AllPages"
                }
                onChange={onSelectRow(row)}
              />
            </TableCell>
          )}
          {columnsIndexes.map((columnIndex: any) => {
            const column = columns[columnIndex];
            const rowConfig = row.rowCellsConfig;
            if (rowConfig && rowConfig[column.fieldName]) {
              const rowCellConfig = rowConfig[column.fieldName];
              if (rowCellConfig.isHidden) {
                return <></>;
              }
              return (
                <TableCell colSpan={rowCellConfig.colSpan}>
                  {onTableRowCellRender(row, columnIndex, index)}
                </TableCell>
              );
            }
            return (
              <TableCell>
                {onTableRowCellRender(row, columnIndex, index)}
              </TableCell>
            );
          })}
        </TableRow>
      );
    }, [row, index, selectionMode, selectType, selectedRows, uniqueRowKeyName]);

    return <>{data}</>;
  }
);
